import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {MessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {AbsoluteDateTimeFormatterContainer} from '../../../../../../libs/shared/ui/src/components/datetime/absoluteDateTimeFormatterContainer';
import {RelativeDateTimeFormatsEnum} from '../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {Dropdown, DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownList} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownLoader} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownLoader';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SeenModel} from '../../../../../models/api/seenModel';
import {InteractiveDateTimeFormatter} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatter';

/*
 * Props.
 */

interface SeenDropdownProps extends DropdownProps {
  message: MessageModel;
  status: AsyncStatusesEnum;
  isClamped: boolean;
  seens?: ReadonlyArray<SeenModel>;
  onRetryClick: () => void;
}

/*
 * Style.
 */

const StyledActivityValueDiv = styled.div`
  grid-area: secondary-content;
  padding-left: 6px;
  width: 100%;

  display: flex;
  justify-content: flex-end;

  color: ${(p) => p.theme.greys.shade50};
  line-height: 20px;
  white-space: nowrap;
`;

const StyledNoActivitiesDiv = styled.div`
  padding: 10px;
  font-style: italic;
  text-align: center;
`;

/*
 * Component.
 */

export const SeenDropdown: FC<SeenDropdownProps> = (props) => (
  <Dropdown {...props} width={260} maxHeight={300}>
    {renderContent(props)}
  </Dropdown>
);

function renderContent(props: SeenDropdownProps) {
  const {message, status, seens, onRetryClick} = props;

  // Loading + Error states.
  if (status !== AsyncStatusesEnum.LOADED) {
    return <DropdownLoader status={status} onRetryClick={onRetryClick} />;
  }

  // No activities loaded state.
  if (!seens || seens.length === 0) {
    return renderNoSeen(message);
  }

  // Activities loaded state.
  return renderSeens(props, seens);
}

function renderNoSeen(message: MessageModel) {
  return (
    <StyledNoActivitiesDiv>
      <Trans id="dropdown-seen-noactivities">
        Seen on <AbsoluteDateTimeFormatterContainer value={message.sentAt} />
      </Trans>
    </StyledNoActivitiesDiv>
  );
}

function renderSeens(props: SeenDropdownProps, activities: ReadonlyArray<SeenModel>) {
  return (
    <>
      <DropdownHeader {...props} title={renderActivityTitle(activities.length, props.isClamped)} />
      <DropdownList {...props}>{activities.map(renderActivity)}</DropdownList>
    </>
  );
}

function renderActivityTitle(activitiesCount: number, isClamped: boolean) {
  if (isClamped) {
    return <Trans id="dropdown-seen-title-limit">Seen {activitiesCount}+ times</Trans>;
  }

  return <Plural id="dropdown-seen-title" value={activitiesCount} one="Seen once" other="Seen # times" />;
}

function renderActivity(seen: SeenModel) {
  // If we have no contact, just render the date/time.
  if (!seen.card) {
    return (
      <DropdownItem key={seen.seenAt}>
        <DropdownItemContent>
          <AbsoluteDateTimeFormatterContainer value={seen.seenAt} />
        </DropdownItemContent>
      </DropdownItem>
    );
  }

  // Otherwise, render the contact as well.
  return (
    <DropdownItem key={seen.seenAt}>
      <DropdownItemContent>{seen.card.name}</DropdownItemContent>
      <StyledActivityValueDiv>
        <InteractiveDateTimeFormatter
          relativeFormat={RelativeDateTimeFormatsEnum.SHORT}
          value={seen.seenAt}
        />
      </StyledActivityValueDiv>
    </DropdownItem>
  );
}
